import type { LDContext } from "launchdarkly-js-sdk-common";
import { getLdClient } from "~/utils/ld-client-singleton";

export function useFeatureFlag(
  key: string,
  defaultValue = false,
): Readonly<Ref<boolean>> {
  return _useFeatureFlag(key, defaultValue);
}

export function useBooleanFeatureFlag(
  key: string,
  defaultValue = false,
): Readonly<Ref<boolean>> {
  return _useFeatureFlag(key, defaultValue);
}

export function useStringFeatureFlag(
  key: string,
  defaultValue = "",
): Readonly<Ref<string>> {
  return _useFeatureFlag(key, defaultValue);
}

function _useFeatureFlag<T extends boolean | string>(
  key: string,
  defaultValue: T,
): Readonly<Ref<T>> {
  const ldClient = getLdClient();
  const flagValue =
    (ldClient?.variation(key, defaultValue) as T | undefined) ?? defaultValue;
  const flagRef = ref(flagValue) as Ref<T>;

  // handle changes to the flag value
  const updateFlagRef = (newFlagValue: T) => {
    flagRef.value = newFlagValue;
  };
  if (ldClient) {
    ldClient.on(`change:${key}`, updateFlagRef);

    // onBeforeUnmount can only be called from inside a component's setup func
    // so we only call it if there is a current component instance.
    // The other significant scenario is from a Nuxt app plugin, in which case
    // there is no reasonable "unmount" callback, so we don't care about
    // manually unsubscribing.
    if (getCurrentInstance()) {
      onBeforeUnmount(() => {
        ldClient.off(`change:${key}`, updateFlagRef);
      });
    }
  }

  // return a readonly version of the flag
  return readonly(flagRef) as Readonly<Ref<T>>;
}

export function useLdContext() {
  const { session } = useAppSession();
  const context = computed<LDContext>(() => {
    if (!session.value.user)
      return {
        kind: "user",
        key: "user-key-anonymous",
        anonymous: true,
      };
    return {
      kind: "user",
      key: session.value.user.email,
      name: session.value.user.displayName,
      email: session.value.user.email,
      custom: {
        customerIds: session.value.customers,
      },
    };
  });
  return context;
}
